import {
  Objectclass,
  Pagination as PaginationType,
  ScrollBar,
  SortOption,
  YesObject
} from '@yes.technology/react-toolkit'
import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FieldSettings } from 'shared/types'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import useInitialStatus from 'status/shared/hooks/useInitialStatus'
import DataTableAreaLine from './DataTableAreaLine'
import { MapColumnToAction } from './DataTables.types'
import Actions from './components/Actions'
import RenderEmbedded from './components/RenderEmbedded'
import TableActions from './components/TableActions/TableActions'
import TableBody from './components/TableBody'
import TableHeader from './components/TableHeader'
import useColumns from './hooks/useColumns'
import useSelectedRowsFromSearchParams from './hooks/useSelectedRowsFromSearchParams'
import { TableContext, TableContextValue } from './hooks/useTableContext'
import useTableContextValue from './hooks/useTableContextValue'
import { TableLayout } from './hooks/useTableLayouts'
import { useIsMobile } from '@yes.technology/react-toolkit'
import {
  ActionColumn,
  ActionColumnsPosition,
  ExtraSelectionAction,
  HierarchyLevel,
  SelectAndLinkOptions,
  SelectionAction
} from './types'

export const defaultActionColumns = [
  {
    title: 'actions',
    component: Actions
  }
]

export type TableActionButton = {
  type: 'view' | 'document' | null
  target: '_self' | '_blank'
  sitemodel?: string
}

export type DataTableConfigProps = {
  layoutClassificationUuid?: string
  hierarchyLevel?: HierarchyLevel
  actionButton?: TableActionButton
  hideRowActionButtons?: boolean
  hideMainCollapsibleContainer?: boolean
  hideSortButtons?: boolean
}

export type DataTableProps = DataTableConfigProps & {
  informational?: boolean
  embedded?: boolean
  idObjectClass: string
  fields: Objectclass['fields']
  objects: YesObject[]
  tableLayout: TableLayout
  isLoading: boolean
  actionColumns?: ActionColumn[]
  actionColumnsPosition?: ActionColumnsPosition
  onSort: (newSortOptions: SortOption[], isDefaultSorting?: boolean) => void
  sortOptions?: SortOption[]
  pagination?: Partial<PaginationType>
  totalObjects?: number
  setCurrentPage: (page: number) => void
  title?: string
  isShowTitle?: boolean
  disableLinkButton?: boolean
  isCollapsibleCard?: boolean
  isShowPDFReports?: boolean
  isShowXLSReport?: boolean
  layoutClassificationUuid?: string
  objectclassQueryString?: string
  displayLayoutSelection?: boolean
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  concatFields?: ConcatFields
  style?: React.CSSProperties
  fieldSettings?: FieldSettings
  allowCollapsibleNavigationCardCustomColors?: boolean
  hideCollapsibleCardArrow?: boolean
  allowHighlightSelectedRow?: boolean
  selectAndLinkOptions?: SelectAndLinkOptions
  primarySelectionAction?: SelectionAction
  extraSelectionActions?: ExtraSelectionAction[]
  insertOnly?: boolean
  mapColumnToAction?: MapColumnToAction
  navSticky?: boolean
  id?: string
  highlightedRows?: string[]
  textFading?: boolean
  hasTitleFading?: boolean
  extendMobileAreaLine?: boolean
  onBeforeViewObject?: () => void
} & Pick<
    TableContextValue,
    | 'objectclass'
    | 'mode'
    | 'setMode'
    | 'changedFieldValues'
    | 'setChangedFieldValues'
    | 'refetchObjects'
    | 'relationCounterpartField'
    | 'customActionButtons'
    | 'hideEditAction'
    | 'hideInsertAction'
    | 'hideSelectAndLinkAction'
    | 'hideDocumentUploadAction'
    | 'selectAndLinkTargetPath'
    | 'uploadAcceptedFileTypes'
    | 'relationColumnName'
  >

const emptyArray: Array<unknown> = []

const DataTable = ({
  idObjectClass,
  objectclass,
  fields,
  objects,
  refetchObjects,
  isLoading,
  actionColumns = defaultActionColumns,
  actionColumnsPosition = 'last',
  onSort,
  tableLayout,
  sortOptions = emptyArray as SortOption[],
  pagination,
  totalObjects,
  setCurrentPage,
  title = '',
  isShowTitle = true,
  isCollapsibleCard = false,
  layoutClassificationUuid,
  hierarchyLevel = 'level_2',
  actionButton,
  mode,
  setMode,
  changedFieldValues,
  setChangedFieldValues,
  relationCounterpartField,
  objectclassQueryString = 'uuid_objectclass',
  displayLayoutSelection = true,
  informational = false,
  embedded = false,
  navigationSliderBackgroundColor = '#004099',
  navigationSliderColor,
  concatFields,
  hideMainCollapsibleContainer = false,
  hideRowActionButtons = false,
  hideSortButtons = false,
  hideEditAction = false,
  hideInsertAction,
  hideSelectAndLinkAction,
  hideDocumentUploadAction = true,
  style,
  fieldSettings,
  customActionButtons,
  allowCollapsibleNavigationCardCustomColors,
  selectAndLinkOptions,
  primarySelectionAction,
  isShowPDFReports,
  isShowXLSReport,
  extraSelectionActions = emptyArray as ExtraSelectionAction[],
  disableLinkButton,
  allowHighlightSelectedRow,
  insertOnly = false,
  mapColumnToAction,
  hideCollapsibleCardArrow,
  navSticky,
  id,
  highlightedRows = [],
  selectAndLinkTargetPath,
  relationColumnName,
  textFading = true,
  uploadAcceptedFileTypes,
  hasTitleFading,
  extendMobileAreaLine,
  onBeforeViewObject
}: DataTableProps) => {
  const [selectedRows, setSelectedRows] = useSelectedRowsFromSearchParams()
  const isMobile = useIsMobile()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (!objects?.length) return
    setSelectedRows((prev) => {
      let changed = false
      const next = { ...prev }
      for (const [uuid, row] of Object.entries(prev)) {
        if (row.value) continue
        const full = objects.find((o) => o.uuid === uuid)
        if (full) {
          next[uuid] = { ...row, des: full.des ?? row.des, value: full }
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [objects, setSelectedRows])

  const hasSelectionAction = !informational && mode === 'view'

  const showColumn =
    !embedded || actionButton?.type === 'document' || !informational

  const amountOfActionColumns =
    showColumn && !hideRowActionButtons
      ? actionColumns.length + (hasSelectionAction ? 1 : 0)
      : 0

  const {
    displayedColumns,
    currentColumnIndex,
    totalColumns,
    amountOfFixedColumns,
    onChangeColumnsPage,
    allColumns
  } = useColumns({
    tableLayout,
    amountOfActionColumns
  })

  const columnsNames = isMobile ? allColumns : displayedColumns

  const tableContextValue = useTableContextValue({
    idObjectClass,
    objectclass,
    fields,
    actionColumns,
    actionColumnsPosition,
    columnsNames,
    amountOfFixedColumns,
    totalColumns,
    objects,
    refetchObjects,
    columnsLayouts: tableLayout.columnsLayouts,
    extraColumns: tableLayout.extraColumns || [],
    readOnlyColumns: tableLayout.readOnlyColumns,
    onSort,
    sortOptions,
    currentColumnIndex,
    pagination,
    totalObjects,
    layoutClassificationUuid,
    hierarchyLevel,
    actionButton,
    selectedRows,
    setSelectedRows,
    mode,
    setMode,
    changedFieldValues,
    setChangedFieldValues,
    relationCounterpartField,
    objectclassQueryString,
    displayLayoutSelection,
    informational,
    embedded,
    navigationSliderBackgroundColor,
    navigationSliderColor,
    allowCollapsibleNavigationCardCustomColors,
    concatFields,
    hideRowActionButtons,
    hideSortButtons,
    hideEditAction,
    hideInsertAction,
    hideSelectAndLinkAction,
    hideDocumentUploadAction,
    onChangeColumnsPage,
    amountOfActionColumns,
    customActionButtons,
    allColumns,
    selectAndLinkOptions,
    primarySelectionAction,
    isShowPDFReports,
    isShowXLSReport,
    extraSelectionActions,
    disableLinkButton,
    allowHighlightSelectedRow,
    insertOnly,
    mapColumnToAction,
    highlightedRows,
    selectAndLinkTargetPath,
    relationColumnName,
    textFading,
    uploadAcceptedFileTypes,
    extendMobileAreaLine,
    objectClassTitle: title,
    onBeforeViewObject
  })

  const initialStatus = useInitialStatus({ objectclassUuid: idObjectClass })

  const tableClassName = `table ${isMobile ? 'mobile' : ''} ${
    isLoading ? 'loading' : ''
  }`

  const tableContainerRef = useRef<HTMLDivElement>(null)

  return (
    <TableContext.Provider value={tableContextValue}>
      <DataTableAreaLine
        {...{
          hierarchyLevel,
          title,
          isShowTitle,
          isCollapsibleCard,
          navigationSliderBackgroundColor,
          navigationSliderColor,
          pagination,
          setCurrentPage,
          embedded,
          hideMainCollapsibleContainer,
          hideCollapsibleCardArrow,
          allowCollapsibleNavigationCardCustomColors,
          id,
          hasTitleFading
        }}
        aria-busy={isLoading}
        aria-live='assertive'
        style={style}
        navSticky={navSticky}
        layoutClassificationUuid={layoutClassificationUuid}
      >
        <RenderEmbedded className='pb-4' embedded={embedded}>
          <div className='row'>
            <div className='col-md-12' ref={tableContainerRef}>
              {!informational && !isLoading && (
                <TableActions
                  {...{
                    layoutClassificationUuid,
                    initialStatus
                  }}
                  tableContainerRef={tableContainerRef}
                />
              )}
              <div className='table-responsive'>
                <ScrollBar scrollMode='unset' style={{ minHeight: 'unset' }}>
                  <table className={tableClassName}>
                    <TableHeader {...{ isLoading }} />
                    <TableBody {...{ isLoading, fieldSettings }} />
                  </table>
                </ScrollBar>
              </div>
            </div>
          </div>
        </RenderEmbedded>
      </DataTableAreaLine>
    </TableContext.Provider>
  )
}

export default DataTable
